/**
 * PUNTOS DE ATENCIÓN PAGE - Attention Points Management
 *
 * Usa useServerDataTable igual que Usuarios para evitar infinite loops
 * cuando cambia la sucursal en el header.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Monitor,
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import type { PuntoAtencion } from "@/hooks/use-turnos";
import { getPuntosAtencionPaginated } from "@/hooks/use-turnos";
import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import {
  useActivatePuntoAtencion,
  useDeactivatePuntoAtencion,
  useDeletePuntoAtencion,
} from "@/hooks/use-turnos";

import { PuntoAtencionFormDialog } from "./components/punto-atencion-form-dialog";

const TIPO_LABELS: Record<string, string> = {
  RECEPCION: "Recepción",
  EXTRACCION: "Extracción",
  CAJA: "Caja",
  ENTREGA: "Entrega",
};

const TIPO_OPTIONS = [
  { value: "RECEPCION", label: "Recepción" },
  { value: "EXTRACCION", label: "Extracción" },
  { value: "CAJA", label: "Caja" },
  { value: "ENTREGA", label: "Entrega" },
];

export default function PuntosAtencionPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  // Contexto de sucursal
  const { sucursalActiva, canViewAllSucursales, isAllSucursalesSelected } = useSucursal();

  // Sucursales para el dropdown de filtro
  const { data: sucursales = [] } = useSucursales();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedPunto, setSelectedPunto] = useState<PuntoAtencion | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Mutations
  const activateMutation = useActivatePuntoAtencion({
    onSuccess: () => {
      toast.success(t("puntosAtencion.toast.activated", { name: selectedPunto?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivatePuntoAtencion({
    onSuccess: () => {
      toast.warning(t("puntosAtencion.toast.deactivated", { name: selectedPunto?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeletePuntoAtencion({
    onSuccess: () => {
      toast.error(t("puntosAtencion.toast.deleted", { name: selectedPunto?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Determinar filtro inicial de sucursal basado en contexto
  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) {
      return "all";
    }
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      return String(sucursalActiva.id);
    }
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  // Action handlers
  const handleEdit = useCallback((punto: PuntoAtencion) => {
    setSelectedPunto(punto);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((punto: PuntoAtencion) => {
    setSelectedPunto(punto);
    setConfirmAction(punto.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((punto: PuntoAtencion) => {
    setSelectedPunto(punto);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  // Column definitions
  const columns: ColumnDef<PuntoAtencion>[] = useMemo(
    () => [
      {
        accessorKey: "codigo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("puntosAtencion.columns.code")} />
        ),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.codigo}</span>,
      },
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("puntosAtencion.columns.name")} />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.nombre}</span>,
      },
      {
        accessorKey: "tipo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("puntosAtencion.columns.type")} />
        ),
        cell: ({ row }) => (
          <Badge variant="outline">{TIPO_LABELS[row.original.tipo] || row.original.tipo}</Badge>
        ),
      },
      {
        accessorKey: "sucursal_nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("puntosAtencion.columns.sucursal")} />
        ),
        cell: ({ row }) => row.original.sucursal_nombre || "-",
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("puntosAtencion.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const punto = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(punto)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(punto)}>
                  {punto.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!punto.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(punto)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Server-side data table hook (igual que Usuarios)
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getPuntosAtencionPaginated,
    queryKey: "puntos-atencion",
    storageKey: "puntos_atencion_page_size",
    filters: [
      { key: "sucursal", defaultValue: "all" },
      { key: "tipo", defaultValue: "all" },
      { key: "is_active", defaultValue: "all", urlParam: "status" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Sincronizar filtro de sucursal cuando cambia el contexto
  useEffect(() => {
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  // Handlers
  const handleCreate = () => {
    setSelectedPunto(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedPunto || !confirmAction) return;

    if (confirmAction === "activate") {
      activateMutation.mutate(selectedPunto.id);
    } else if (confirmAction === "deactivate") {
      deactivateMutation.mutate(selectedPunto.id);
    } else if (confirmAction === "delete") {
      deleteMutation.mutate(selectedPunto.id);
    }

    setIsConfirmOpen(false);
    setSelectedPunto(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("puntosAtencion.toast.created") : t("puntosAtencion.toast.updated"));
    setSelectedPunto(null);
    refetch();
  };

  // Column labels for view options
  const columnLabels = {
    codigo: t("puntosAtencion.columns.code"),
    nombre: t("puntosAtencion.columns.name"),
    tipo: t("puntosAtencion.columns.type"),
    sucursal_nombre: t("puntosAtencion.columns.sucursal"),
    is_active: t("puntosAtencion.columns.status"),
  };

  // Mobile card render
  const renderMobileCard = useCallback(
    (punto: PuntoAtencion) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-medium text-sm">{punto.codigo}</span>
            <span className="font-medium">{punto.nombre}</span>
            <Badge variant={punto.is_active ? "default" : "secondary"} className="shrink-0">
              {punto.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {TIPO_LABELS[punto.tipo] || punto.tipo}
            </Badge>
            {punto.sucursal_nombre && (
              <span className="text-sm text-muted-foreground">{punto.sucursal_nombre}</span>
            )}
          </div>
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleEdit(punto)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleToggleStatus(punto)}>
                  {punto.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!punto.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(punto)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Determinar si se puede crear (necesita sucursal específica)
  const canCreate =
    !isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Monitor className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("puntosAtencion.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("puntosAtencion.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button
            size={isMobile ? "sm" : "default"}
            onClick={handleCreate}
            disabled={!canCreate}
            title={!canCreate ? t("puntosAtencion.selectBranchFirst") : undefined}
          >
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("puntosAtencion.newPunto")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("common.filter")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("puntosAtencion.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Filtro de sucursal */}
              {isAllSucursalesSelected ? (
                <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder={t("puntosAtencion.filterBySucursal")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("puntosAtencion.allSucursales")}</SelectItem>
                    {sucursales.map((sucursal) => (
                      <SelectItem key={sucursal.id} value={String(sucursal.id)}>
                        {sucursal.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50 text-sm">
                  <span className="text-muted-foreground mr-2">
                    {t("puntosAtencion.columns.sucursal")}:
                  </span>
                  <span className="font-medium">{sucursalActiva?.nombre || "-"}</span>
                </div>
              )}

              {/* Filtro de tipo */}
              <Select value={filters.tipo} onValueChange={(v) => setFilter("tipo", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder={t("puntosAtencion.filterByType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("puntosAtencion.allTypes")}</SelectItem>
                  {TIPO_OPTIONS.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filtro de estado */}
              <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder={t("puntosAtencion.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("puntosAtencion.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Botón limpiar filtros */}
              {(() => {
                const hasEditableFilters = isAllSucursalesSelected
                  ? hasActiveFilters
                  : searchTerm !== "" || filters.tipo !== "all" || filters.is_active !== "all";
                return hasEditableFilters ? (
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={clearFilters}
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null;
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info message - solo cuando todas las sucursales están seleccionadas */}
      {isAllSucursalesSelected && (
        <div className="flex items-center gap-2 rounded-md bg-muted p-4 text-muted-foreground">
          <AlertCircle className="h-5 w-5" />
          <span>{t("puntosAtencion.selectBranchFirst")}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">{t("puntosAtencion.list")}</CardTitle>
              <CardDescription>
                {t("puntosAtencion.showingResults", { count: totalCount })}
              </CardDescription>
            </div>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("puntosAtencion.noPuntos")}
            hideOnTablet={["sucursal_nombre", "tipo"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <PuntoAtencionFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        punto={selectedPunto}
        sucursalId={canCreate ? sucursalActiva?.id : undefined}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("puntosAtencion.confirmActivate")
            : confirmAction === "delete"
              ? t("puntosAtencion.confirmDelete")
              : t("puntosAtencion.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("puntosAtencion.confirmActivateDesc", { name: selectedPunto?.nombre })
            : confirmAction === "delete"
              ? t("puntosAtencion.confirmDeleteDesc", { name: selectedPunto?.nombre })
              : t("puntosAtencion.confirmDeactivateDesc", { name: selectedPunto?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
